import React, { useState } from 'react';
import { NewChatPicker } from '../chat/NewChatPicker';
import { PersonaAvatar } from '../personas/PersonaAvatar';
import { getTierPresentation } from '../../utils/personaTier';
import { InvitePeerDialog } from './InvitePeerDialog';
import type { Persona } from '../../types/persona';

const TRIGGER_CLS =
  'w-full mb-3 flex items-center gap-2 rounded border px-2 py-1.5 text-sm text-left ' +
  'bg-white border-gray-300 text-gray-900 dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100 ' +
  'hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40';

/**
 * InvitePeerHost — sources the peer chooser (NewChatPicker, single-select) and renders the
 * InvitePeerDialog. Owns the selected persona so the Dialog stays prop-driven and free of the
 * usePersonas → api.ts import.meta chain (NewChatPicker reaches usePersonas; that lives HERE).
 * Mounted conditionally by PersonaConversationSurface (turn-invite / trail-join / reconsider).
 */
export const InvitePeerHost: React.FC<{
  sessionId: string;
  fromTurnId: string | null;
  onClose: () => void;
  onProposed?: (proposalId: string) => void;
  reconsider?: boolean;
  /** Shared spines P2 — trail-join mode (camp ⋯ menu). */
  inviteTrailId?: string | null;
}> = ({ sessionId, fromTurnId, onClose, onProposed, reconsider, inviteTrailId }) => {
  const [selected, setSelected] = useState<Persona | null>(null);

  const peerField = (
    <NewChatPicker
      variant="select"
      onSelect={setSelected}
      trigger={({ open, isOpen }) => (
        <button
          type="button"
          data-testid="invite-peer-trigger"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          onClick={open}
          className={TRIGGER_CLS}
        >
          {selected ? (
            <span className="flex items-center gap-2 min-w-0">
              <PersonaAvatar name={selected.name} specialization={selected.primarySpecialization} size="small" />
              <span className="truncate">{selected.name}</span>
              <span aria-hidden="true" className="ml-auto text-gray-400">{getTierPresentation(selected).glyph}</span>
              <span className="sr-only">{getTierPresentation(selected).ariaLabel}</span>
            </span>
          ) : (
            <span className="text-gray-400 dark:text-gray-500">Choose a peer…</span>
          )}
        </button>
      )}
    />
  );

  return (
    <InvitePeerDialog
      sessionId={sessionId}
      fromTurnId={fromTurnId}
      inviteeId={selected?.id ?? ''}
      peerField={peerField}
      onClose={onClose}
      onProposed={onProposed}
      reconsider={reconsider}
      inviteTrailId={inviteTrailId}
    />
  );
};
